import { useState, useContext } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  BarChart,
  Bell,
  Eye,
  EyeOff,
  Loader2,
  Store,
  Package
} from "lucide-react";
import type { LoginCredentials } from "@/types/api";
import { AuthContext } from "@/context/AuthContext";

const Index = () => {
  const navigate = useNavigate();
  const { login, loading } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState<LoginCredentials>({
    email: "",
    password: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData({
      ...loginData,
      [name]: value
    });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
        await login(loginData);
        navigate("/dashboard");
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading indicator while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <main className="min-h-screen w-full flex flex-col">
      {/* Navbar */}
      <div className="flex justify-between items-center px-8 py-4 bg-primary text-primary-foreground">
        <Link to="/" className="flex items-center gap-2 font-bold text-xl">
          <Store size={24} />
          Jaya Maruthi Store
        </Link>

        <div className="flex gap-6">
          <Button variant="ghost" asChild>
            <Link to="/login">Login</Link>
          </Button>
          {/*<Button variant="ghost" asChild>
            <Link to="/register">Register</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/contact">Contact</Link>
          </Button>*/}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Section - Features */}
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl font-bold text-primary mb-4">
                Streamline Your Inventory Management
              </h1>

              <p className="text-lg text-muted-foreground mb-8">
                Take control of your inventory with our powerful, easy-to-use management system.
              </p>
            </div>

            <div className="space-y-4">
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <Package size={24} className="text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Real-time Tracking</h3>
                    <p className="text-muted-foreground">
                      Monitor stock levels, track product movement, and receive low stock alerts.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <BarChart size={24} className="text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Insightful Analytics</h3>
                    <p className="text-muted-foreground">
                      Gain valuable insights with comprehensive reports and visual analytics.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <Bell size={24} className="text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Smart Notifications</h3>
                    <p className="text-muted-foreground">
                      Stay informed with customizable alerts for critical inventory events.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Section - Login Form */}
          <div className="flex items-center">
            <Card className="w-full">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">Login to Your Account</CardTitle>
                <CardDescription className="text-center">
                  Enter your credentials to access your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@example.com"
                      value={loginData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={handleInputChange}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2"
                        onClick={handleTogglePassword}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        <span className="sr-only">
                          {showPassword ? "Hide password" : "Show password"}
                        </span>
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </form>
              </CardContent>
              {/* 
              <CardFooter className="flex justify-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-primary font-medium hover:underline">
                    Register here
                  </Link>
                </p>
              </CardFooter>*/}
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center border-t">
        <p className="text-sm text-muted-foreground">
          © 2025 Sukri Software
        </p>
      </div>
    </main>
  );
};

export default Index;